import { useCallback, useState } from 'react';
import { Button, Card } from 'react-bootstrap';

import { faLink } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import optainCameraImg from 'src/assets/img/optain-camera.png';

import useLocalStore from 'src/store/useLocalStore';
import useRemoteBoundStore from 'src/store/useRemoteBoundStore';

import { Api } from 'src/models/Api.model';
import CameraConnection from 'src/models/Camera/CameraConnection';
import { getLocaleCode, Language } from 'src/models/Language.model';

import useUiTranslation from 'src/hooks/useUiTranslation';

import { captureError } from 'src/utils/errorReporting';

export type ConnectCameraListProps = {
  ipList: string[];
};

export default function ConnectCameraList({ ipList }: ConnectCameraListProps) {
  const { setCameraData } = useRemoteBoundStore();
  const { setCameraIP } = useLocalStore();
  const [connectingToIP, setConnectingToIP] = useState<string>('');

  const { tPages, tCommon, language } = useUiTranslation();

  const handleConnectCamera = useCallback(
    async (ip: string) => {
      try {
        setConnectingToIP(ip);
        const cameraData = await CameraConnection.loadCameraData(
          ip,
          tCommon('message.error.cameraIpMissingMsg'),
        );
        await CameraConnection.getInstance().say(
          'Camera successfully connected.',
        );

        setCameraData(cameraData);
        setCameraIP(ip);
      } catch (error) {
        captureError(error, 'Failed to connect to camera');

        Api.alertBox({
          title: tCommon('bootbox.title.Error'),
          message: tCommon('message.error.failedToConnectToCamera'),
          locale: getLocaleCode(language as Language),
          type: 'error',
        });
      } finally {
        setConnectingToIP('');
      }
    },
    [language, setCameraData, setCameraIP, tCommon],
  );

  return (
    <div className="d-flex flex-wrap gap-4">
      {ipList.map((ip) => (
        <Card
          key={ip}
          border="dark"
          className="p-3 mb-3"
          style={{ borderRadius: 20, borderWidth: 2, width: '23rem' }}
        >
          <div className="d-flex justify-content-between px-2">
            <div className="d-flex flex-column gap-2">
              <div>
                <h5>FC162</h5>
                <h5>({ip})</h5>
              </div>
              <Button
                variant="primary"
                onClick={() => {
                  handleConnectCamera(ip);
                }}
                disabled={connectingToIP === ip}
              >
                <FontAwesomeIcon icon={faLink} className="me-2" />
                {connectingToIP === ip
                  ? `${tPages('settings.cameraModes.cameraConnection.connecting')}...`
                  : tPages('settings.cameraModes.cameraConnection.connect')}
              </Button>
            </div>
            <div>
              <img
                src={optainCameraImg}
                style={{ width: 120 }}
                alt={tPages('settings.tabs.Camera')}
              />
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}
